import { useId, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  api,
  ApiError,
  type ConnectorSummary,
  type FieldDef,
  type UserStructuredProps,
} from "../../lib/api";

import { FileDropzone, FileFormatHint } from "../FileDropzone";
import { useToast } from "../toast/ToastProvider";
import { cn } from "../../lib/cn";
import { litmusCronFromConfig, describeSchedule } from "../AddSourceDialog";
import {
  isStructuredPropertiesValid,
  StructuredPropertiesEditor,
} from "../StructuredPropertiesEditor";
import {
  SourceForm,
  emptyValuesFromSchema,
  getMissingRequiredFields,
  type SourceFormValues,
} from "../SourceForm";

export const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export const MONTH_DATES: Array<{ value: string; label: string }> = [
  ...Array.from({ length: 28 }, (_, i) => ({
    value: String(i + 1),
    label: `${i + 1}${ordinalSuffix(i + 1)} of each month`,
  })),
  { value: "last", label: "Last day of each month" },
];

export const COMMON_TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "America/Honolulu",
  "America/Toronto",
  "America/Mexico_City",
  "America/Sao_Paulo",
  "America/Buenos_Aires",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Rome",
  "Europe/Madrid",
  "Europe/Amsterdam",
  "Europe/Warsaw",
  "Europe/Moscow",
  "Europe/Istanbul",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Dhaka",
  "Asia/Bangkok",
  "Asia/Singapore",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Asia/Jakarta",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Australia/Perth",
  "Pacific/Auckland",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Africa/Lagos",
];

export function ordinalSuffix(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return "th";
  if (mod10 === 1) return "st";
  if (mod10 === 2) return "nd";
  if (mod10 === 3) return "rd";
  return "th";
}

type ImportMode = "one-time" | "scheduled";
type SyncFrequency = "monthly" | "daily" | "weekly";

interface ConfigureConnectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectorSummary: ConnectorSummary;
  onPickDifferentConnector?: () => void;
}

export function ConfigureConnectorModal({
  open,
  onOpenChange,
  connectorSummary,
  onPickDifferentConnector,
}: ConfigureConnectorModalProps) {
  const qc = useQueryClient();
  const { show } = useToast();
  const uid = useId();
  const [mode, setMode] = useState<ImportMode>("one-time");
  const [name, setName] = useState("");
  const [formValues, setFormValues] = useState<SourceFormValues>(() =>
    emptyValuesFromSchema(connectorSummary.schema),
  );
  const [uploadedFileId, setUploadedFileId] = useState("");
  const [structuredProperties, setStructuredProperties] = useState<UserStructuredProps>({});
  const [scheduledFilePath, setScheduledFilePath] = useState("");
  const [frequency, setFrequency] = useState<SyncFrequency>("monthly");
  const [syncDay, setSyncDay] = useState("Monday");
  const [syncDate, setSyncDate] = useState("1");
  const [syncTime, setSyncTime] = useState("02:00");
  const [syncTimezone, setSyncTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [error, setError] = useState<string | null>(null);

  const isScheduled = mode === "scheduled";
  const fileFields = connectorSummary.schema.fields.filter((f) => f.type === "file");
  const hasFileFields = fileFields.length > 0;
  const fileExtensions = fileFields.flatMap((f) => f.accept ?? []);

  // File fields are rendered manually so the label and checksum note can vary by mode.
  const schemaWithoutFiles = {
    ...connectorSummary.schema,
    fields: connectorSummary.schema.fields.filter((f) => f.type !== "file"),
  };

  const scheduleCron = isScheduled
    ? litmusCronFromConfig(frequency, syncTime, syncTimezone, syncDay, syncDate)
    : "";
  const scheduleDesc = isScheduled
    ? describeSchedule(frequency, syncTime, syncTimezone, syncDay, syncDate)
    : "";
  const canSubmit =
    name.trim() !== "" &&
    (!hasFileFields || (isScheduled ? scheduledFilePath.trim() !== "" : uploadedFileId !== ""));

  function reset() {
    setMode("one-time");
    setName("");
    setFormValues(emptyValuesFromSchema(connectorSummary.schema));
    setUploadedFileId("");
    setStructuredProperties({});
    setScheduledFilePath("");
    setFrequency("monthly");
    setSyncDay("Monday");
    setSyncDate("1");
    setSyncTime("02:00");
    setSyncTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    setError(null);
  }

  function cleanupStagedUpload() {
    if (!uploadedFileId || isScheduled) return;
    fetch(`/api/v1/uploads/${uploadedFileId}`, {
      method: "DELETE",
      credentials: "same-origin",
    }).catch(() => {});
  }

  function handleClose() {
    cleanupStagedUpload();
    reset();
    onOpenChange(false);
  }

  const create = useMutation({
    mutationFn: (body: unknown) => api.post("/api/v1/sources", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sources"] });
      show(
        "Source added",
        name.trim() ? `"${name.trim()}" is ready` : "New source created",
        "success",
      );
      reset();
      onOpenChange(false);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Create failed";
      setError(msg);
      show("Create failed", msg, "error");
    },
  });

  function submit() {
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    if (hasFileFields) {
      if (isScheduled && !sanitizeFilePath(scheduledFilePath)) {
        setError("Enter the absolute path to the file on the agent host.");
        return;
      }
      if (!isScheduled && !uploadedFileId) {
        setError("Please select a file.");
        return;
      }
    }

    const missing = getMissingRequiredFields(schemaWithoutFiles, formValues, false);
    if (missing.length > 0) {
      setError(`Required: ${missing.join(", ")}`);
      return;
    }

    if (!isStructuredPropertiesValid(structuredProperties)) {
      setError("Fix the invalid or conflicting static metadata fields before continuing.");
      return;
    }

    setError(null);

    const config: Record<string, unknown> = {
      ...formValues.config,
      ...(hasFileFields
        ? { file_path: isScheduled ? sanitizeFilePath(scheduledFilePath) : uploadedFileId }
        : {}),
      ...(isScheduled
        ? {
            sync_mode: "scheduled",
            sync_frequency: frequency,
            sync_time: syncTime,
            sync_timezone: syncTimezone,
            sync_day: syncDay,
            sync_date: syncDate,
          }
        : {}),
    };

    create.mutate({
      name: name.trim(),
      connectorId: connectorSummary.id,
      config,
      secrets: formValues.secrets,
      structuredProperties,
      scheduleCron: scheduleCron.trim(),
      enabled: true,
    });
  }

  const fileFieldDef: FieldDef = {
    key: "file_path",
    label: isScheduled ? "Project / Backup File" : "Source Export File",
    type: "file",
    required: true,
    accept: fileExtensions.length > 0 ? fileExtensions : undefined,
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[1400] bg-ink/40 backdrop-blur-sm" />

        <Dialog.Content
          aria-describedby={`${uid}-desc`}
          className="
            fixed left-1/2 top-[72px] z-[1401]
            w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2
            rounded-soft border border-rule bg-surface shadow-cardLift
            max-h-[calc(100vh-96px)] overflow-y-auto no-scrollbar
          "
        >
          <div className="flex items-start justify-between gap-4 px-7 pt-6 pb-0">
            <div>
              <Dialog.Title asChild>
                <h2 className="font-display text-[20px] font-semibold tracking-tight text-ink">
                  Configure {connectorSummary.displayName}
                </h2>
              </Dialog.Title>
              <p
                id={`${uid}-desc`}
                className="mt-1 flex items-center gap-1.5 text-[12px] text-litmus-dark-green-700"
              >
                <ShieldIcon />
                Secrets are AES-256-GCM encrypted at rest.
              </p>
            </div>
            <Dialog.Close asChild>
              <button
                aria-label="Close"
                className="mt-0.5 shrink-0 rounded p-1 text-ash hover:bg-paper hover:text-ink"
              >
                <CloseIcon />
              </button>
            </Dialog.Close>
          </div>

          <div className="space-y-5 px-7 pb-7 pt-5">
            <label className="block">
              <span className="mb-1.5 block font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-ash">
                Name
              </span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={`${connectorSummary.displayName} on plant-01`}
                className="input"
                autoFocus
                aria-required="true"
              />
            </label>

            <div>
                <div
                  className="flex w-full overflow-hidden rounded border border-rule bg-paper p-1 gap-1"
                  role="group"
                  aria-label="Import mode"
                >
                  {(
                    [
                      {
                        value: "one-time" as ImportMode,
                        label: "One-time import",
                        subtitle: "Ingest once now",
                      },
                      {
                        value: "scheduled" as ImportMode,
                        label: "Scheduled sync",
                        subtitle: "Keep it fresh",
                      },
                    ] as const
                  ).map((opt) => {
                    const active = mode === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setMode(opt.value)}
                        aria-pressed={active}
                        className={cn(
                          "flex-1 rounded py-2.5 px-4 text-center transition-all select-none",
                          active
                            ? "bg-white text-ink shadow-sm cursor-default"
                            : "bg-transparent text-ash cursor-pointer hover:text-ink",
                        )}
                      >
                        <div
                          className={cn(
                            "text-[13px] leading-tight",
                            active ? "font-semibold" : "font-medium",
                          )}
                        >
                          {opt.label}
                        </div>
                        <div className="mt-0.5 text-[11px] leading-tight text-ash">
                          {opt.subtitle}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2.5 text-[12.5px] leading-relaxed text-ash">
                  {isScheduled
                    ? "The agent re-reads the selected project/backup file (or .zip) on a schedule and re-ingests changed metadata — no manual re-uploads."
                    : "Upload a vendor export and ingest its metadata once. Re-run anytime with a single refresh."}
                </p>
            </div>

            {hasFileFields && !isScheduled && (
              <div>
                <div className="mb-2">
                  <span className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-ash">
                    Source Export File <span className="text-rose-600">*</span>
                  </span>
                </div>
                <FileDropzone
                  def={fileFieldDef}
                  value={uploadedFileId}
                  onChange={setUploadedFileId}
                  connectorId={connectorSummary.id}
                />
              </div>
            )}

            {hasFileFields && isScheduled && (
              <ScheduledFileInput
                value={scheduledFilePath}
                onChange={setScheduledFilePath}
                accept={fileExtensions.length > 0 ? fileExtensions : undefined}
              />
            )}

            <SourceForm
              schema={schemaWithoutFiles}
              value={formValues}
              onChange={setFormValues}
              connectorId={connectorSummary.id}
            />

            <StructuredPropertiesEditor
              value={structuredProperties}
              onChange={setStructuredProperties}
            />

            {isScheduled && (
              <SyncFrequencySection
                uid={uid}
                frequency={frequency}
                syncDay={syncDay}
                syncDate={syncDate}
                syncTime={syncTime}
                syncTimezone={syncTimezone}
                scheduleDesc={scheduleDesc}
                onFrequencyChange={setFrequency}
                onDayChange={setSyncDay}
                onDateChange={setSyncDate}
                onTimeChange={setSyncTime}
                onTimezoneChange={setSyncTimezone}
              />
            )}

            {error && (
              <div
                role="alert"
                className="rounded-md border border-rose-500/30 bg-rose-50 px-3 py-2 text-[13px] text-rose-700"
              >
                {error}
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-rule pt-5">
              {onPickDifferentConnector ? (
                <button
                  type="button"
                  onClick={() => {
                    handleClose();
                    onPickDifferentConnector();
                  }}
                  className="text-[12px] text-ash hover:text-ink"
                >
                  ← Pick a different connector
                </button>
              ) : (
                <span />
              )}

              <div className="flex gap-2">
                <button type="button" onClick={handleClose} className="btn-secondary">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={!canSubmit || create.isPending}
                  className="btn-primary"
                  title={!canSubmit ? "Fill in required fields to continue" : undefined}
                >
                  {create.isPending
                    ? "Creating…"
                    : isScheduled
                      ? "Create scheduled source →"
                      : "Create source →"}
                </button>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export interface SyncFrequencySectionProps {
  uid: string;
  frequency: SyncFrequency;
  syncDay: string;
  syncDate: string;
  syncTime: string;
  syncTimezone: string;
  scheduleDesc: string;
  onFrequencyChange: (f: SyncFrequency) => void;
  onDayChange: (d: string) => void;
  onDateChange: (d: string) => void;
  onTimeChange: (t: string) => void;
  onTimezoneChange: (tz: string) => void;
}

export function SyncFrequencySection({
  uid,
  frequency,
  syncDay,
  syncDate,
  syncTime,
  syncTimezone,
  scheduleDesc,
  onFrequencyChange,
  onDayChange,
  onDateChange,
  onTimeChange,
  onTimezoneChange,
}: SyncFrequencySectionProps) {
  const FREQ_OPTIONS = [
    { value: "daily" as SyncFrequency, label: "Daily", sub: "Every day" },
    { value: "weekly" as SyncFrequency, label: "Weekly", sub: "Once a week" },
    { value: "monthly" as SyncFrequency, label: "Monthly", sub: "Once a month" },
  ] as const;

  return (
    <section
      aria-labelledby={`${uid}-freq-heading`}
      className="space-y-4 rounded-md border border-rule bg-paper p-4"
    >
      <h3
        id={`${uid}-freq-heading`}
        className="flex items-center gap-1.5 font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-ash"
      >
        <SyncIcon />
        Sync Frequency
      </h3>

      <div className="flex gap-2" role="group" aria-label="Sync frequency">
        {FREQ_OPTIONS.map((opt) => {
          const active = frequency === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onFrequencyChange(opt.value)}
              aria-pressed={active}
              className={cn(
                "flex flex-1 flex-col items-center gap-1.5 rounded-md border px-3 py-3 text-center transition-all select-none",
                active
                  ? "border-litmus-dark-green-700 bg-litmus-dark-green-700/5 cursor-default"
                  : "border-rule bg-surface cursor-pointer hover:border-ash/40",
              )}
            >
              <CalendarIcon className={active ? "text-litmus-dark-green-700" : "text-ash"} />
              <div
                className={cn(
                  "text-[13px] font-semibold leading-tight",
                  active ? "text-litmus-dark-green-700" : "text-ink",
                )}
              >
                {opt.label}
              </div>
              <div className="text-[11px] leading-tight text-ash">{opt.sub}</div>
            </button>
          );
        })}
      </div>

      <div className="flex gap-3">
        {frequency === "monthly" && (
          <label className="flex flex-1 flex-col gap-1">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ash">
              On Day of Month
            </span>
            <select
              value={syncDate}
              onChange={(e) => onDateChange(e.target.value)}
              className="input"
            >
              {MONTH_DATES.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
        )}

        {frequency === "weekly" && (
          <label className="flex flex-1 flex-col gap-1">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ash">
              On Day
            </span>
            <select value={syncDay} onChange={(e) => onDayChange(e.target.value)} className="input">
              {DAYS_OF_WEEK.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>
        )}

        {frequency === "daily" && <div className="flex-1" />}

        <label className="flex flex-1 flex-col gap-1">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ash">
            At Time
          </span>
          <input
            type="time"
            value={syncTime}
            onChange={(e) => onTimeChange(e.target.value)}
            className="input"
          />
        </label>

        <label className="flex flex-[1.6] flex-col gap-1">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ash">
            Timezone
          </span>
          <select
            value={syncTimezone}
            onChange={(e) => onTimezoneChange(e.target.value)}
            className="input"
          >
            {!COMMON_TIMEZONES.includes(syncTimezone) && (
              <option value={syncTimezone}>{syncTimezone}</option>
            )}
            {COMMON_TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </label>
      </div>

      {scheduleDesc && <p className="text-[12px] leading-relaxed text-ash">{scheduleDesc}</p>}
    </section>
  );
}

// sanitizeFilePath strips surrounding whitespace and any wrapping double or
// single quotes that users sometimes paste along with a path copied from
// Explorer / Finder (e.g. "C:\file.csv" → C:\file.csv).
export function sanitizeFilePath(raw: string): string {
  let s = raw.trim();
  if (
    (s.startsWith('"') && s.endsWith('"')) ||
    (s.startsWith("'") && s.endsWith("'"))
  ) {
    s = s.slice(1, -1).trim();
  }
  return s;
}

interface ScheduledFileInputProps {
  value: string;
  onChange: (path: string) => void;
  accept?: string[];
}

export function ScheduledFileInput({ value, onChange, accept }: ScheduledFileInputProps) {
  return (
    <div>
      <span className="mb-1.5 block font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-ash">
        File Path on Agent Host <span className="text-rose-600">*</span>
      </span>

      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="C:\exports\tags.csv  or  /home/user/file.csv  or  /nfs/data/file.csv"
        className="input font-mono text-[12px]"
        spellCheck={false}
        aria-label="File path on agent host"
        aria-required="true"
      />

      {accept && accept.length > 0 && <FileFormatHint accept={accept} />}

      <p className="mt-1.5 text-[11.5px] leading-relaxed text-ash">
        Paste the absolute path to the file on the agent host. For Docker/Kubernetes use the path
        inside the container (e.g. <code className="font-mono">/mnt/volume/file.csv</code>). The
        agent reads from this location on every scheduled run and skips ingestion if the file is unchanged.
      </p>
    </div>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" fill="none" aria-hidden className="shrink-0">
      <path
        d="M8 1.5L2.5 4v4c0 2.9 2.3 5.6 5.5 6.5C11.2 13.6 13.5 10.9 13.5 8V4L8 1.5z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <path
        d="M5.5 8l1.75 1.75L10.5 6"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden>
      <path
        d="M3 3l10 10M13 3L3 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SyncIcon() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" fill="none" aria-hidden>
      <path
        d="M13.5 2.5v4h-4M2.5 13.5v-4h4"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.5 6.5A6 6 0 003.4 4.4M2.5 9.5a6 6 0 0010.1 2.1"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden className={className}>
      <rect
        x="1.5"
        y="3"
        width="13"
        height="11.5"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.3"
      />
      <path
        d="M1.5 6.5h13M5 1.5v3M11 1.5v3"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  );
}
